// Ricerca MVP client-side (Batch 3). Logica pura: nessun side-effect, nessun
// DOM, nessun fetch. Opera SOLO sull'array `photos` ricevuto (lo stesso della
// mappa/timeline, già filtrato backend-side per privacy): i privati pre-unlock
// non sono nel dataset, quindi non sono cercabili. Nessun path speciale per la
// privacy qui dentro. Testabile in isolamento (scripts/test-search.mjs).

import type { PhotoSummary } from "./api";

// Segni diacritici combinanti Unicode (U+0300–U+036F): rimossi dopo NFD per una
// ricerca accent-insensitive (es. "città" matcha "citta").
const COMBINING_MARKS = /[̀-ͯ]/g;

/** Normalizza il testo: rimozione diacritici + lowercase + trim. */
export function normalizeSearchText(s: string): string {
  return s.normalize("NFD").replace(COMBINING_MARKS, "").toLowerCase().trim();
}

/** Spezza la query in termini normalizzati non vuoti (separati da spazi). */
export function tokenizeSearchQuery(query: string): string[] {
  const norm = normalizeSearchText(query);
  return norm ? norm.split(/\s+/).filter(Boolean) : [];
}

/** Testo cercabile di una foto: description + place_label + categories, normalizzato. */
function searchableText(p: Pick<PhotoSummary, "description" | "place_label" | "categories">): string {
  const parts = [p.description ?? "", p.place_label ?? "", ...(p.categories ?? [])];
  return normalizeSearchText(parts.join(" "));
}

/**
 * True se la foto soddisfa TUTTI i termini della query (AND), cercati su
 * description/place_label/categories. Query vuota → true (nessun vincolo).
 * Campi null/undefined gestiti senza errori.
 */
export function matchesQuery(p: PhotoSummary, query: string): boolean {
  const tokens = tokenizeSearchQuery(query);
  if (tokens.length === 0) return true;
  const hay = searchableText(p);
  return tokens.every((t) => hay.includes(t));
}

/**
 * Filtra le foto per query. Casi identità (ritorna l'array invariato):
 *  - query vuota / solo spazi;
 *  - query tutta-cifre: candidato PIN, riservato allo sblocco — la ricerca NON
 *    filtra i contenuti (coerente col routing numerico → PIN della search bar).
 */
export function searchPhotos(photos: PhotoSummary[], query: string): PhotoSummary[] {
  const trimmed = query.trim();
  if (!trimmed) return photos;
  if (/^\d+$/.test(trimmed)) return photos;
  return photos.filter((p) => matchesQuery(p, trimmed));
}
